import { useEffect, useMemo } from 'react';
import { SquareButton2 } from '../atoms';
import houseIcon from '@/assets/property-icons/house-icon.svg'
import { CreateRentChargeInput, RentChargeTypeOption } from '@/graphql/generated';
import { useCreateContractContext, useUserLanguage } from '@/customHooks';
import { NormalFormNumberField } from '../molecules';
import { CreateRentChargeFormControlled } from './CreateRentChargeFormControlled';
import { ToggleSwitch } from '../atoms/ToggleSwitch';

export const SelectContractRentCharges = () => {
  const userLanguage = useUserLanguage()

  const {
    state: contractState,
    setRentCharges,
    setDayForExecuteCharges,
    setAutoGenerateRentCharge,
    setMonthlyChargeAmount,
    setButtonDisabled,
    setButtonEnabled,
  } = useCreateContractContext()

  const rentCharges = useMemo<CreateRentChargeInput[]>(
    () => contractState.rentCharges || [],
    [contractState.rentCharges]
  )

  const onActionHandle = (index: number, data: CreateRentChargeInput) => {
    const newRentCharges = rentCharges.map((charge, i) => (i === index ? data : charge));
    setRentCharges(newRentCharges);
  }

  const onDeleteHandle = (indexToRemove: number) => {
    const newRentCharges = rentCharges.filter((_, index) => index !== indexToRemove);
    setRentCharges(newRentCharges);
  }

  const onAddHandle = () => {
    const chargeDay = parseInt(contractState.dayForExecuteCharges || '1', 10);
    const startDate = new Date(contractState.startDate as Date);

    const executionDate = new Date(startDate);
    const startDay = startDate.getDate();

    if (chargeDay < startDay) {
      // If charge day is in the past for the current month, move to the next month
      executionDate.setMonth(executionDate.getMonth() + 1);
    }

    executionDate.setDate(chargeDay);

    const newCharge: CreateRentChargeInput = {
      rentChargeType: RentChargeTypeOption.RentIncome,
      description: '',
      amountValue: '',
      executionDate: executionDate,
    };

    const newRentCharges = [...rentCharges, newCharge];
    setRentCharges(newRentCharges);
  }

  const handleDayChange = (value: string | null) => {
    let day = parseInt(value || '1', 10);
    if (day < 1) day = 1;
    if (day > 30) day = 30;
    setDayForExecuteCharges(day.toString());
  }

  useEffect(() => {
    const isAuto = contractState.isAutoGenerateRentCharge;
    const hasMonthlyAmount = !!contractState.monthlyChargeAmount && parseFloat(contractState.monthlyChargeAmount) > 0;
    const hasRentCharges = rentCharges.length > 0;

    if (isAuto) {
      if (hasMonthlyAmount) {
        setButtonEnabled();
      } else {
        setButtonDisabled();
      }
    } else {
      if (hasRentCharges) {
        setButtonEnabled();
      } else {
        setButtonDisabled();
      }
    }
  }, [
    contractState.isAutoGenerateRentCharge,
    contractState.monthlyChargeAmount,
    rentCharges,
    setButtonEnabled,
    setButtonDisabled
  ]);

  return (
    <div className="flex flex-col items-center w-full">
      <div className="bg-[var(--color-light)] rounded-lg shadow-[0_5px_10px_rgba(0,0,0,0.1)] p-8 w-full max-w-[650px] min-w-[320px] mx-auto mb-12 box-border">
        <div className="flex items-center justify-between gap-4 mb-4">
          <span>{userLanguage === 'es' ? 'Generar cargo de renta automáticamente' : 'Auto-generate rent charge'}</span>
          <ToggleSwitch
            defaultChecked={contractState.isAutoGenerateRentCharge}
            onChange={setAutoGenerateRentCharge}
          />
        </div>
        <NormalFormNumberField
          label={userLanguage === 'es' ? 'Monto fijo mensual de renta' : 'Fixed monthly rent amount'}
          name="monthlyChargeAmount"
          value={contractState.monthlyChargeAmount || '0'}
          onChange={(value) => setMonthlyChargeAmount(value || '0')}
          readOnly={!contractState.isAutoGenerateRentCharge}
        />
        <NormalFormNumberField
          label={userLanguage === 'es' ? 'Día para aplicar cargos por renta' : 'Day for applying rent charges'}
          name="daysToExecuteRentCharges"
          value={contractState.dayForExecuteCharges || '1'}
          onChange={handleDayChange}
        />
      </div>

      <div className="w-full flex flex-col items-center gap-4">
        {
          rentCharges.map((charge, index) => (
            <CreateRentChargeFormControlled
              key={index}
              id={index}
              onChange={(data) => onActionHandle(index, data)}
              defaultData={charge}
              dropDownMenuActions={{
                delete: (id) => onDeleteHandle(id)
              }}
            />
          ))
        }
      </div>

      <div className="mt-8 flex justify-center w-full">
        <SquareButton2
          action={onAddHandle}
        >
          <>
            <img src={houseIcon} alt="" />
            <span>Add Charge</span>
          </>
        </SquareButton2>
      </div>
    </div>
  )
}
